import { useEffect, useState, ChangeEvent } from 'react';
import Cookies from 'universal-cookie';
import Converters from './Converters';
import InputField from './InputField';
import Card, { CollasableCard } from './Cards';
import { useTranslation } from 'react-i18next';

const cookies = new Cookies();

interface OgState {
  originalBrixGravity: string | number;
  correctionFactor: string | number;
  originalBLGGravity: string;
}

interface FgState {
  originalBrixGravity2: string | number;
  correctionFactor2: string | number;
  finalBrixGravity: string | number;
  finalBLGGravity: string;
}

function getFGFromBrix(brixOg: number, brixFg: number): number {
  return 1.0000 - 0.0044993 * brixOg + 0.011774 * brixFg +
    0.00027581 * Math.pow(brixOg, 2) - 0.0012717 * Math.pow(brixFg, 2) -
    0.0000072800 * Math.pow(brixOg, 3) + 0.000063293 * Math.pow(brixFg, 3);
}

function calcGravity(originalBrixGravity: string | number, correctionFactor: string | number): OgState {
  if (Number(originalBrixGravity) > 0 && Number(correctionFactor) > 0) {
    return { originalBrixGravity, correctionFactor, originalBLGGravity: (Number(originalBrixGravity) / Number(correctionFactor)).toFixed(2) };
  }
  return { originalBrixGravity, correctionFactor, originalBLGGravity: 'NaN' };
}

function calcFinalGravity(originalBrixGravity2: string | number, correctionFactor2: string | number, finalBrixGravity: string | number): FgState {
  if (Number(originalBrixGravity2) > 0 && Number(correctionFactor2) > 0 && Number(finalBrixGravity) > 0) {
    const blg = Number(originalBrixGravity2) / Number(correctionFactor2);
    const fg = Number(finalBrixGravity) / Number(correctionFactor2);
    const FGinSG = getFGFromBrix(blg, fg);
    const trueFG = Converters.gravityToPlato(FGinSG);
    return { originalBrixGravity2, correctionFactor2, finalBrixGravity, finalBLGGravity: isNaN(trueFG) ? 'NaN' : trueFG.toFixed(2) };
  }
  return { originalBrixGravity2, correctionFactor2, finalBrixGravity, finalBLGGravity: 'NaN' };
}

export default function Refractometer() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('refractometer.pageTitle');
  }, [t]);

  const [og, setOg] = useState<OgState>(() => calcGravity(
    cookies.get('refractometer.originalBrixGravity') || 12,
    cookies.get('refractometer.correctionFactor') || 1.04,
  ));
  const [fg, setFg] = useState<FgState>(() => calcFinalGravity(
    cookies.get('refractometer.originalBrixGravity2') || 12,
    cookies.get('refractometer.correctionFactor2') || 1.04,
    cookies.get('refractometer.finalBrixGravity') || 5,
  ));

  function handleOgChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    cookies.set(`refractometer.${name}`, value);
    setOg(calcGravity(
      name === 'originalBrixGravity' ? value : og.originalBrixGravity,
      name === 'correctionFactor' ? value : og.correctionFactor,
    ));
  }

  function handleFgChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    cookies.set(`refractometer.${name}`, value);
    setFg(calcFinalGravity(
      name === 'originalBrixGravity2' ? value : fg.originalBrixGravity2,
      name === 'correctionFactor2' ? value : fg.correctionFactor2,
      name === 'finalBrixGravity' ? value : fg.finalBrixGravity,
    ));
  }

  return (
    <div>
      <CollasableCard title={t('refractometer.infoTitle')}>
        <p className="text-muted-foreground text-sm">
          {t('refractometer.infoLine1')}
        </p>
      </CollasableCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card title={t('refractometer.wortTitle')}>
          <InputField name="originalBrixGravity" label={t('refractometer.ogBrix')} step="0.1" value={og.originalBrixGravity} unit="Brix" onChange={handleOgChange} />
          <InputField name="correctionFactor" label={t('refractometer.correctionFactor')} step="0.01" value={og.correctionFactor} onChange={handleOgChange} />
          <InputField label={t('refractometer.ogCorrected')} value={og.originalBLGGravity} unit="BLG" readOnly />
        </Card>

        <Card title={t('refractometer.beerTitle')}>
          <InputField name="originalBrixGravity2" label={t('refractometer.ogBrix')} step="0.1" value={fg.originalBrixGravity2} unit="Brix" onChange={handleFgChange} />
          <InputField name="finalBrixGravity" label={t('refractometer.fgBrix')} step="0.1" value={fg.finalBrixGravity} unit="Brix" onChange={handleFgChange} />
          <InputField name="correctionFactor2" label={t('refractometer.correctionFactor')} step="0.01" value={fg.correctionFactor2} onChange={handleFgChange} />
          <InputField label={t('refractometer.fgCorrected')} value={fg.finalBLGGravity} unit="BLG" readOnly />
        </Card>
      </div>
    </div>
  );
}
